<template>
  <v-app>
    <template v-if="!isLoginPage">
      <!-- App Bar -->
      <v-app-bar app flat color="white" elevation="1" class="px-4" height="90">
        <!-- Nav Icon for Mobile -->
        <v-app-bar-nav-icon v-if="!isDesktop" @click="drawer = !drawer" />

        <!-- Logo -->
        <img src="./assets/logo_light2.png" alt="Logo" width="150" class="mx-4" />

        <v-spacer />

        <!-- Desktop User Info -->
        <div v-if="isDesktop" class="app-bar-user-info">
          <LanguageSwitcher />
          <span class="font-weight-medium ml-4 mr-3">{{ username }}</span>

          <!-- Logout Button -->
          <v-btn icon class="mx-4 logout-btn" @click="logout" title="Logout">
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </div>

        <!-- Mobile User Info Toggle -->
        <v-btn icon v-if="!isDesktop" class="ml-2" @click="userInfoDrawer = !userInfoDrawer" title="Toggle User Info">
          <v-icon>mdi-account-outline</v-icon>
        </v-btn>


      </v-app-bar>

      <!-- Navigation Drawer -->
      <v-navigation-drawer v-model="drawer" :temporary="!isDesktop" :permanent="isDesktop" app color="white"
        width="250">
        <v-list nav dense>
          <v-list-item value="Dashboard" :class="{ 'active-item': route.name === 'Dashboard' }"
            @click="navigateTo('Dashboard')">
            <div class="d-flex align-center ga-4">
              <v-icon class="mr-2" :color="route.name === 'Dashboard' ? 'white' : 'primary'">
                mdi-view-dashboard
              </v-icon>
              <v-list-item-title class="text-left">
                {{ $t('dashboard') }}
              </v-list-item-title>
            </div>
          </v-list-item>
          
          <!-- Machines Group -->
          <v-list-group v-model="machinesGroupOpen" no-action>
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" class="pl-2">
                <div class="d-flex align-center ga-4">
                  <v-icon class="mr-2" :color="isMachinesGroupActive ? '#40c1ac' : 'primary'">mdi-cog</v-icon>
                  <v-list-item-title class="text-left" :style="{ color: isMachinesGroupActive ? '#40c1ac' : 'inherit' }">
                    {{ $t('machines') }}
                  </v-list-item-title>
                </div>
              </v-list-item>
            </template>

            <v-list-item v-for="([title, icon]) in machines" :key="title" :value="title"
              :class="{ 'active-item': $route.name === 'MachineDetails' && $route.params.name === title }"
              @click="selectMachine(title)" class="pl-4">
              <div class="d-flex align-center ga-4">
                <v-icon :color="$route.name === 'MachineDetails' && $route.params.name === title ? 'white' : 'primary'">
                  {{ icon }}
                </v-icon>
                <v-list-item-title class="text-left">
                  {{ title }}
                </v-list-item-title>
              </div>
            </v-list-item>
          </v-list-group>
          
        </v-list>
      </v-navigation-drawer>

      <!-- User Info Drawer (Mobile) -->
      <v-navigation-drawer v-model="userInfoDrawer" temporary app color="white" width="220" right>
        <div class="pa-4 ml-0 d-flex flex-column align-center">
          <LanguageSwitcher />
          <div class="mt-4 font-weight-medium">{{ username }}</div>
        </div>
        <!-- Logout Button for Mobile -->
        <v-btn icon v-if="!isDesktop" class="ml-2" @click="logout" title="Logout">
          <v-icon>mdi-logout</v-icon>
        </v-btn>
      </v-navigation-drawer>
    </template>

    <!-- Main Content -->
    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import LanguageSwitcher from './components/LanguageSwitcher.vue';

const API_HOST = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';

export default {
  components: {
    LanguageSwitcher,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const drawer = ref(true);
    const userInfoDrawer = ref(false);
    const isDesktop = ref(window.innerWidth >= 960);
    const machinesGroupOpen = ref(true);
    const machines = ref([]);
    const machinesData = ref([]);
    const selectedItem = ref(route.params.name || null);
    const username = ref('');
    
    const isMachinesGroupActive = computed(() =>
      machines.value.some(([title]) => route.params.name === title)
    );

    const isLoginPage = computed(() => route.path === '/login');

    const onResize = () => {
      isDesktop.value = window.innerWidth >= 960;
      drawer.value = isDesktop.value;
      userInfoDrawer.value = false;
    };

    const fetchMachines = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        machines.value = [];
        return;
      }
      try {
        const url = API_HOST.trim().replace(/\/$/, '') + '/api/machines/active'
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        response.data.sort((a, b) => a.nameMachine.localeCompare(b.nameMachine));
        response.data.forEach((machine) => {
          if (Array.isArray(machine.lastShifts)) {
            machine.lastShifts.reverse();
          }
        });
        machines.value = response.data.map((m) => [m.nameMachine, 'mdi-robot-industrial']);
        machinesData.value = response.data;
      } catch (error) {
        console.error('Failed to fetch machines:', error.response?.status, error.message);
      }
    };

    const navigateTo = (name) => {
      router.push({ name });
    };

    const selectMachine = (title) => {
      selectedItem.value = title;
      router.push(`/machines/${title}`);
    };

    const logout = () => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUsername');
      localStorage.removeItem('userRole');
      router.push('/login');
    };

    watch(
      () => route.params.name,
      (newName) => {
        if (newName) selectedItem.value = newName;
      }
    );

    watch(
      () => route.fullPath,
      () => {
        if (!isLoginPage.value) {
          const savedUsername = localStorage.getItem('authUsername');
          username.value = savedUsername || 'User';
          fetchMachines();
        } else {
          machines.value = [];
          username.value = '';
          machinesData.value = [];
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      window.addEventListener('resize', onResize);
      onResize();

      if (!isLoginPage.value) {
        fetchMachines();
        const savedUsername = localStorage.getItem('authUsername');
        username.value = savedUsername || 'User';
      } else {
        machines.value = [];
        username.value = '';
        machinesData.value = [];
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize);
    });

    return {
      drawer,
      userInfoDrawer,
      isDesktop,
      isLoginPage,
      machines,
      machinesData,
      machinesGroupOpen,
      isMachinesGroupActive,
      navigateTo,
      selectMachine,
      route,
      selectedItem,
      logout,
      username,
    };
  },
};
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.active-item {
  background-color: #40c1ac !important;
  color: white !important;

  .v-icon {
    color: white !important;
  }
}

.v-list-item__content {
  text-align: left;
}

.app-bar-user-info {
  display: flex;
  align-items: center;
}

.app-bar-user-info span {
  margin: 0 8px;
}

.v-list-group__items {
  --indent-padding: calc(3px);
}

.logout-btn {
  border-radius: 50%;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.logout-btn:hover {
  transform: scale(1.15);
}

.logout-btn .v-btn__overlay,
.logout-btn .v-btn__underlay {
  display: none;
}

.logout-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  /* Soft hover effect */
  transition: background-color 0.2s ease;
}

.logout-btn:hover .v-icon {
  color: #40c1ac !important;
}

.v-container {
    user-select: none;
}

.v-container .v-btn,
.v-container .v-icon {
    user-select: auto; /* кнопки можно выделять, если нужно */
}

.v-list-item--active .v-list-item__overlay,
.v-list-item--active .v-list-item__underlay {
  background-color: transparent !important;
  opacity: 0 !important;
}

</style>
